import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Button } from "@/components/ui/button";
import {
  ArrowRight,
  Hotel,
  UtensilsCrossed,
  Truck,
  Building2,
  Store,
} from "lucide-react";

const industries = [
  {
    icon: Hotel,
    title: "Hospitality & Hotels",
    description:
      "Premium eco-friendly tableware for hotels, resorts, and hospitality chains looking to enhance their sustainability credentials.",
    features: ["Room service", "Restaurants", "Events", "Buffets"],
  },
  {
    icon: UtensilsCrossed,
    title: "Catering & Events",
    description:
      "Elegant, sustainable solutions for catering companies and event organizers who want to minimize environmental impact.",
    features: ["Weddings", "Corporate events", "Festivals", "Conferences"],
  },
  {
    icon: Truck,
    title: "Food Delivery Chains",
    description:
      "Durable, leak-proof packaging designed for food delivery operations that need reliable, eco-conscious solutions.",
    features: ["QSR chains", "Cloud kitchens", "Aggregators", "Dark stores"],
  },
  {
    icon: Building2,
    title: "Institutional Supply",
    description:
      "Large-scale sustainable packaging for hospitals, schools, corporate cafeterias, and government institutions.",
    features: ["Hospitals", "Schools", "Corporates", "Government"],
  },
  {
    icon: Store,
    title: "Eco-conscious Retailers",
    description:
      "Partner with retailers committed to offering sustainable alternatives to their environmentally aware customers.",
    features: ["Organic stores", "Health food", "Eco boutiques", "Supermarkets"],
  },
];

const IndustriesPage = () => {
  return (
    <div data-testid="industries-page" className="noise-overlay">
      <Helmet>
        <title>Industries We Serve | Eco-Friendly Packaging for HoReCa, Retail & Food Service</title>
        <meta name="description" content="Prithira Global supplies biodegradable tableware to hotels, restaurants, catering, food trucks, retail chains, and institutional buyers across India and globally." />
        <meta name="keywords" content="biodegradable packaging for restaurants, eco friendly tableware for hotels, sustainable catering supplies, food service packaging India" />
        <link rel="canonical" href="https://www.prithiraglobal.com/industries" />
        <meta property="og:title" content="Industries We Serve | Prithira Global" />
        <meta property="og:description" content="Eco-friendly packaging for hotels, restaurants, catering, retail, and institutional buyers — India and worldwide." />
        <meta property="og:url" content="https://www.prithiraglobal.com/industries" />
      </Helmet>
      {/* Hero */}
      <section className="pt-32 pb-20 bg-sand-200/30">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto text-center">
            <span className="caption text-primary mb-4 block">Our Markets</span>
            <h1 className="heading-xl text-stone-900 mb-8">
              Industries We <span className="italic">Serve</span>
            </h1>
            <p className="body-lg text-stone-600 max-w-3xl mx-auto">
              From local hospitality to global importers, we provide sustainable
              packaging solutions tailored to diverse industry needs.
            </p>
          </div>
        </div>
      </section>

      {/* Industries Grid */}
      <section className="section-spacing bg-white">
        <div className="container-custom">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {industries.map((industry, index) => (
              <div
                key={index}
                data-testid={`industry-card-${index}`}
                className="group bg-white rounded-2xl border border-stone-100 p-8 hover:shadow-lg hover:border-primary/20 transition-all duration-300"
              >
                <div className="w-16 h-16 rounded-xl bg-primary/10 flex items-center justify-center mb-6 group-hover:bg-primary/20 transition-colors">
                  <industry.icon
                    className="h-8 w-8 text-primary"
                    strokeWidth={1.5}
                  />
                </div>
                <h3 className="font-heading text-xl text-stone-900 mb-3">
                  {industry.title}
                </h3>
                <p className="text-stone-600 font-light text-sm leading-relaxed mb-6">
                  {industry.description}
                </p>
                <div className="flex flex-wrap gap-2">
                  {industry.features.map((feature, idx) => (
                    <span
                      key={idx}
                      className="bg-sand-200/50 px-3 py-1 rounded-full text-xs text-stone-600"
                    >
                      {feature}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="section-spacing bg-white">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-16">
              <span className="caption text-primary mb-4 block">Why Prithira</span>
              <h2 className="heading-lg text-stone-900 mb-6">
                Your Trusted <span className="italic">Supply</span> Partner
              </h2>
            </div>
            <div className="grid md:grid-cols-2 gap-6">
              {[
                {
                  title: "Reliable Supply",
                  desc: "Consistent quality and availability through our established network of production partners.",
                },
                {
                  title: "Competitive Pricing",
                  desc: "Direct sourcing and efficient operations allow us to offer excellent value.",
                },
                {
                  title: "Custom Solutions",
                  desc: "We work with you to develop products that meet your specific requirements.",
                },
                {
                  title: "Quality Assurance",
                  desc: "Every batch undergoes rigorous testing to ensure consistent standards.",
                },
              ].map((item, index) => (
                <div
                  key={index}
                  className="bg-sand-200/30 rounded-xl p-6 border border-sand-300/50"
                >
                  <h4 className="font-heading text-lg text-stone-900 mb-2">
                    {item.title}
                  </h4>
                  <p className="text-stone-600 font-light text-sm">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-spacing bg-primary text-white">
        <div className="container-custom text-center">
          <h2 className="heading-lg mb-6">
            Ready to <span className="italic">Collaborate</span>?
          </h2>
          <p className="body-lg text-white/80 mb-10 max-w-2xl mx-auto">
            Whether you're a hotel chain, distributor, or importer, we'd love to
            discuss how Prithira can support your sustainability goals.
          </p>
          <Link to="/partner">
            <Button
              data-testid="industries-cta-btn"
              className="rounded-full px-8 py-6 text-base font-medium bg-white text-primary hover:bg-stone-100 btn-lift"
            >
              Start a Conversation
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default IndustriesPage;
